import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { fetchPieceByJob, type ContentPiece } from '../../lib/contentPieces';
import {
  answerSpecDecision,
  setOutlineOverride,
  setSectionsOverride, setKeywordOverride,
  approveBuild,
  approveSpec,
  cancelAiJob,
  cancelReadyBuild,
  createTaskFromParked,
  deleteUnwoundJob,
  fetchJobsByTask,
  isOffQueueStatus,
  reviseAiJob,
  sendBackToBuild,
  sendBuildBackToSpec,
  setAutoApprove,
  updateClientNote,
  updateQueueNote,
  submitApprovedBuild,
  type AiJob,
  type DecisionAnswers,
} from '../../lib/aiJobs';
import type { OutlineHeading, PageSection } from '../../lib/aiJobSummary';
import { useUser } from '../../context/UserContext';
import { NoteNotSubmittedDialog } from './operator/SubmissionNote';
import { useSubmissionNote } from './operator/useSubmissionNote';
import {
  aiJobChipLabel,
  aiJobKind,
  AI_JOB_STATUS_LABEL,
  aiJobStatusTone,
  formatClaimElapsed,
  isAiJobInProgress,
} from '../../lib/aiJobLabels';
import { formatRelativeDate } from '../../utils/dateUtils';
import { fetchTracesForJob, subscribeTracesForJob } from '../../lib/aiJobTraces';
import {
  fetchSummaryVersions,
  operatorSummaryForJob,
  subscribeSummaryVersions,
  summaryChangesSince,
  summaryChipTags,
  type SummaryVersion,
} from '../../lib/aiJobSummary';
import type { AiJobTrace } from '../../lib/supabase.types';
import { Badge, Icon } from '../ui';
import JobOperatorPanel from './operator/JobOperatorPanel';
import SummaryTags from './operator/SummaryTags';
import { handoffAuthor } from '../../lib/aiJobHandoff';
import { uploadJobAttachments } from '../../lib/aiJobAttachments';

interface AiJobChipsProps {
  taskId?: string | null;
  job?: AiJob | null;
  refreshKey?: number;
  style?: React.CSSProperties;
  panelOnly?: boolean;
  initialOpenJobId?: string | null;
  onClosePanel?: () => void;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function AiJobChips({
  taskId,
  job = null,
  refreshKey = 0,
  style,
  panelOnly = false,
  initialOpenJobId = null,
  onClosePanel,
}: AiJobChipsProps) {
  const navigate = useNavigate();
  const { user } = useUser();
  const resolvedTaskId = taskId ?? job?.task_id ?? null;
  const desiredOpenId = initialOpenJobId ?? (panelOnly ? job?.id ?? null : null);
  const [jobs, setJobs] = useState<AiJob[]>(() => (job ? [job] : []));
  const [openJobId, setOpenJobId] = useState<string | null>(() => desiredOpenId);
  const [reply, setReply] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  /** Optional note that rides with Approve spec / Approve build (not a send-back). */
  const [handoffDraft, setHandoffDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmCancelBuild, setConfirmCancelBuild] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [uploading, setUploading] = useState<'note' | 'reply' | null>(null);
  const [traces, setTraces] = useState<AiJobTrace[]>([]);
  const noteFileRef = useRef<HTMLInputElement>(null);
  const replyFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    if (panelOnly && desiredOpenId) {
      setOpenJobId(desiredOpenId);
    } else {
      setOpenJobId(null);
    }
    setReply('');
    setError(null);
    if (job) {
      setJobs((prev) => (prev.some((row) => row.id === job.id) ? prev : [job, ...prev]));
    }
    (async () => {
      if (!resolvedTaskId) {
        if (!cancelled && !job) setJobs([]);
        return;
      }
      try {
        const rows = await fetchJobsByTask(resolvedTaskId);
        if (!cancelled) setJobs(rows);
      } catch (err) {
        console.error('Error loading AI jobs:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedTaskId, refreshKey, job, panelOnly, desiredOpenId]);

  const openJob =
    jobs.find((j) => j.id === openJobId) ?? (job && job.id === openJobId ? job : null);
  // Client feedback jobs: Submit Note under the client note, and Submitted asks while it isn't posted.
  const submissionNote = useSubmissionNote(openJob);
  const [noteGateOpen, setNoteGateOpen] = useState(false);

  // Any handler that resolves the report — approve, cancel, reply/send-back —
  // closes the full-screen panel. Once the job moves on, what's on screen is a
  // stale read of a stage that no longer exists. Edits that leave you in the
  // same stage (notes, decision answers, auto-approve) keep it open.
  const closePanel = () => {
    setOpenJobId(null);
    onClosePanel?.();
  };

  useEffect(() => {
    setReply('');
    setHandoffDraft('');
    setError(null);
    setBusy(false);
    setConfirmCancel(false);
    setConfirmCancelBuild(false);
    setConfirmDelete(false);
    setConfirmSubmit(false);
  }, [openJobId]);

  const openQueueNote = openJob?.queue_note ?? '';
  useEffect(() => {
    setNoteDraft(openQueueNote);
  }, [openJob?.id, openQueueNote]);

  useEffect(() => {
    if (!openJob?.id) {
      setTraces([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const rows = await fetchTracesForJob(openJob.id);
      if (!cancelled) setTraces(rows);
    };
    load();
    const unsubscribe = subscribeTracesForJob(openJob.id, load);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [openJob?.id]);

  // Summary history: every stage write, for the Versions tab and the
  // changed-since-last highlights on the overview.
  const [versions, setVersions] = useState<SummaryVersion[]>([]);
  useEffect(() => {
    if (!openJob?.id) {
      setVersions([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await fetchSummaryVersions(openJob.id);
        if (!cancelled) setVersions(rows);
      } catch (err) {
        console.warn('No summary versions for job:', err);
      }
    };
    load();
    const unsubscribe = subscribeSummaryVersions(openJob.id, load);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [openJob?.id]);

  // Content jobs: the article the writer handed in (content_pieces), if any.
  // Re-checked whenever the job row changes so the button appears once the
  // build lands. Errors (e.g. table not migrated yet) just mean no button.
  const [piece, setPiece] = useState<ContentPiece | null>(null);
  useEffect(() => {
    if (!openJob?.id || openJob.kind !== 'content') {
      setPiece(null);
      return;
    }
    let cancelled = false;
    fetchPieceByJob(openJob.id)
      .then((row) => {
        if (!cancelled) setPiece(row);
      })
      .catch((err) => {
        console.warn('No content piece for job:', err);
        if (!cancelled) setPiece(null);
      });
    return () => {
      cancelled = true;
    };
  }, [openJob?.id, openJob?.kind, openJob?.updated_at]);

  const refreshOpenJob = async (updated: AiJob) => {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
  };

  const handleToggleAutoApprove = async (next: boolean) => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await setAutoApprove(openJob.id, next);
      await refreshOpenJob(updated);
    } catch (err) {
      console.error('Error toggling auto-approve:', err);
      setError(err instanceof Error ? err.message : 'Failed to update auto-approve');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveNote = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateQueueNote(openJob.id, noteDraft);
      await refreshOpenJob(updated);
      setNoteDraft(updated.queue_note);
    } catch (err) {
      console.error('Error saving queue note:', err);
      setError(err instanceof Error ? err.message : 'Failed to save the note');
    } finally {
      setBusy(false);
    }
  };

  // Upload files and append them as markdown; both humans (AiMarkdown) and
  // the agents reading ai_jobs get the same public media URLs. Images embed
  // via r2-notes-upload; other files (.md, .pdf, …) go through r2-job-upload
  // and land as links.
  const uploadNoteFiles = async (target: 'note' | 'reply', files: File[]) => {
    if (!openJob || uploading) return;
    const images = files.filter((f) => f.type.startsWith('image/'));
    const docs = files.filter((f) => !f.type.startsWith('image/'));
    if (images.length === 0 && docs.length === 0) return;
    setUploading(target);
    setError(null);
    try {
      const lines: string[] = [];
      if (images.length > 0) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not signed in');
        const payload = await Promise.all(
          images.map(async (f, i) => ({
            fileName: f.name || `pasted-image-${i + 1}.png`,
            fileType: f.type,
            fileSize: f.size,
            fileData: await readAsBase64(f),
          }))
        );
        const res = await fetch('/.netlify/functions/r2-notes-upload', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ jobId: openJob.id, files: payload }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || `Upload failed (${res.status})`);
        for (const f of json.files as Array<{ fileName: string; publicUrl: string }>) {
          lines.push(`![${f.fileName}](${f.publicUrl})`);
        }
      }
      if (docs.length > 0) {
        for (const f of await uploadJobAttachments(openJob.id, docs)) {
          lines.push(`[${f.name}](${f.url})`);
        }
      }
      const markdown = lines.join('\n');
      const append = (prev: string) => (prev.trim() ? `${prev.replace(/\s+$/, '')}\n\n${markdown}` : markdown);
      if (target === 'note') setNoteDraft(append);
      else setReply(append);
    } catch (err) {
      console.error('Error uploading note file:', err);
      setError(err instanceof Error ? err.message : 'File upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleNotePaste = (target: 'note' | 'reply') => (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      uploadNoteFiles(target, files);
    }
  };

  const handleFilePick = (target: 'note' | 'reply') => (e: React.ChangeEvent<HTMLInputElement>) => {
    uploadNoteFiles(target, Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleRevise = async () => {
    if (!openJob) return;
    const note = reply.trim();
    if (!note || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await reviseAiJob(openJob.id, note);
      await refreshOpenJob(updated);
      setReply('');
      closePanel();
    } catch (err) {
      console.error('Error revising AI job:', err);
      setError(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setBusy(false);
    }
  };

  const handleApproveSpec = async (answers: DecisionAnswers = {}) => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await approveSpec(openJob.id, answers, { note: handoffDraft, by: handoffAuthor(user) });
      await refreshOpenJob(updated);
      setHandoffDraft('');
      closePanel();
    } catch (err) {
      console.error('Error approving spec:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve spec');
    } finally {
      setBusy(false);
    }
  };

  const handleSendBackToBuild = async () => {
    if (!openJob) return;
    const note = reply.trim();
    if (!note || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await sendBackToBuild(openJob.id, note);
      await refreshOpenJob(updated);
      setReply('');
      closePanel();
    } catch (err) {
      console.error('Error sending job back to build:', err);
      setError(err instanceof Error ? err.message : 'Failed to send back to build');
    } finally {
      setBusy(false);
    }
  };

  const handleAnswerDecision = async (decisionId: string, answer: string | null) => {
    if (!openJob) return;
    setError(null);
    try {
      const updated = await answerSpecDecision(openJob.id, decisionId, answer);
      await refreshOpenJob(updated);
    } catch (err) {
      console.error('Error answering decision:', err);
      setError(err instanceof Error ? err.message : 'Failed to save the answer');
      throw err;
    }
  };

  const handleSaveOutline = async (headings: OutlineHeading[] | null, note: string | null) => {
    if (!openJob) return;
    setError(null);
    try {
      const updated = await setOutlineOverride(openJob.id, headings, note);
      await refreshOpenJob(updated);
    } catch (err) {
      console.error('Error saving outline:', err);
      setError(err instanceof Error ? err.message : 'Failed to save the outline');
      throw err;
    }
  };

  const handleSaveSections = async (sections: PageSection[] | null) => {
    if (!openJob) return;
    setError(null);
    try {
      const updated = await setSectionsOverride(openJob.id, sections);
      await refreshOpenJob(updated);
    } catch (err) {
      console.error('Error saving sections:', err);
      setError(err instanceof Error ? err.message : 'Failed to save the sections');
      throw err;
    }
  };

  const handleSaveKeyword = async (keyword: { primary: string; secondary: string[] } | null) => {
    if (!openJob) return;
    setError(null);
    try {
      const updated = await setKeywordOverride(openJob.id, keyword);
      await refreshOpenJob(updated);
    } catch (err) {
      console.error('Error saving keyword:', err);
      setError(err instanceof Error ? err.message : 'Failed to save the keyword');
      throw err;
    }
  };

  const handleSaveClientNote = async (markdown: string) => {
    if (!openJob) return;
    setError(null);
    try {
      const updated = await updateClientNote(openJob.id, markdown);
      await refreshOpenJob(updated);
    } catch (err) {
      console.error('Error saving the client note:', err);
      setError(err instanceof Error ? err.message : 'Failed to save the note');
      throw err;
    }
  };

  const handleMakeTask = async (text: string, dueDate: string) => {
    if (!openJob) return;
    if (!user?.id) throw new Error('Not signed in');
    setError(null);
    try {
      await createTaskFromParked(openJob, text, user.id, dueDate);
    } catch (err) {
      console.error('Error filing parked item as a task:', err);
      setError(err instanceof Error ? err.message : 'Failed to make the task');
      throw err;
    }
  };

  const handleCancel = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await cancelAiJob(openJob.id);
      await refreshOpenJob(updated);
      setConfirmCancel(false);
      closePanel();
    } catch (err) {
      console.error('Error cancelling spec:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel spec');
    } finally {
      setBusy(false);
    }
  };

  const handleApproveBuild = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await approveBuild(openJob.id, { note: handoffDraft, by: handoffAuthor(user) });
      await refreshOpenJob(updated);
      setHandoffDraft('');
      closePanel();
    } catch (err) {
      console.error('Error approving build:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve build');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelBuild = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await cancelReadyBuild(openJob.id);
      await refreshOpenJob(updated);
      setConfirmCancelBuild(false);
      closePanel();
    } catch (err) {
      console.error('Error cancelling ready build:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel ready build');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const deletedId = openJob.id;
      await deleteUnwoundJob(deletedId);
      setJobs((prev) => prev.filter((j) => j.id !== deletedId));
      setConfirmDelete(false);
      closePanel();
    } catch (err) {
      console.error('Error deleting unwound job:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete job');
    } finally {
      setBusy(false);
    }
  };

  const handleSendBack = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await sendBuildBackToSpec(openJob.id);
      await refreshOpenJob(updated);
      closePanel();
    } catch (err) {
      console.error('Error sending build back to spec:', err);
      setError(err instanceof Error ? err.message : 'Failed to send back to spec');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitted = async () => {
    if (!openJob || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await submitApprovedBuild(openJob.id);
      setJobs((prev) => prev.filter((j) => j.id !== updated.id));
      setConfirmSubmit(false);
      closePanel();
    } catch (err) {
      console.error('Error submitting approved build:', err);
      setError(err instanceof Error ? err.message : 'Failed to mark submitted');
    } finally {
      setBusy(false);
    }
  };

  const visibleJobs = jobs.filter((row) => !isOffQueueStatus(row.status));
  if (!panelOnly && visibleJobs.length === 0) return null;

  return (
    <>
      {panelOnly ? null : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', ...style }}>
        {visibleJobs.map((row) => {
          const jobKind = aiJobKind(row.status);
          const isSpec = jobKind === 'spec';
          return (
            <button
              key={row.id}
              type="button"
              className="hover-wash pressable"
              onClick={() => setOpenJobId(row.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                background: isSpec ? 'var(--accent-soft)' : 'var(--status-warn-soft)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <Icon
                name="zap"
                size={16}
                style={{ color: isSpec ? 'var(--accent-text)' : 'var(--status-warn)', flexShrink: 0 }}
              />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {aiJobChipLabel(row)}
                </span>
                <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                  {isAiJobInProgress(row.status) && row.claimed_by
                    ? `${row.claimed_by} · ${formatClaimElapsed(row.claimed_at)} · v${row.version}`
                    : `${AI_JOB_STATUS_LABEL[row.status]} · v${row.version} · ${formatRelativeDate(row.updated_at)}`}
                </span>
                <SummaryTags tags={summaryChipTags(row)} style={{ marginTop: '4px' }} />
              </span>
              <Badge tone={aiJobStatusTone(row.status)}>{AI_JOB_STATUS_LABEL[row.status]}</Badge>
              <Icon name="chevron-right" size={14} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
            </button>
          );
        })}
      </div>
      )}

      {/* Full-screen operator page. State and handlers stay here; the panel is the view. */}
      {openJob ? (
        <JobOperatorPanel
          job={openJob}
          summary={operatorSummaryForJob(openJob)}
          traces={traces}
          piece={piece}
          onClose={closePanel}
          versions={versions}
          changes={summaryChangesSince(openJob, versions)}
          actions={{
            reply,
            setReply,
            handoffDraft,
            setHandoffDraft,
            noteDraft,
            setNoteDraft,
            busy,
            error,
            uploading,
            confirmCancel,
            setConfirmCancel,
            confirmCancelBuild,
            setConfirmCancelBuild,
            confirmDelete,
            setConfirmDelete,
            confirmSubmit,
            setConfirmSubmit: (v: boolean) => (v && submissionNote.needsGate ? setNoteGateOpen(true) : setConfirmSubmit(v)),
            noteFileRef,
            replyFileRef,
            onPaste: handleNotePaste,
            onFilePick: handleFilePick,
            onSaveNote: handleSaveNote,
            onToggleAutoApprove: handleToggleAutoApprove,
            onRevise: handleRevise,
            onApproveSpec: handleApproveSpec,
            onCancel: handleCancel,
            onApproveBuild: handleApproveBuild,
            onCancelBuild: handleCancelBuild,
            onDelete: handleDelete,
            onSendBack: handleSendBack,
            onSendBackToBuild: handleSendBackToBuild,
            onSubmitted: handleSubmitted,
            onViewPiece: (row) => navigate(`/content/pieces/${row.id}`),
            onMakeTask: handleMakeTask,
            onAnswerDecision: handleAnswerDecision,
            onSaveClientNote: handleSaveClientNote,
            onSaveOutline: handleSaveOutline,
            onSaveSections: handleSaveSections,
            onSaveKeyword: handleSaveKeyword,
            submissionNote: submissionNote.state.kind === 'none' ? undefined : submissionNote,
          }}
        />
      ) : null}
      <NoteNotSubmittedDialog
        open={noteGateOpen && !!openJob}
        control={submissionNote}
        busy={busy}
        onClose={() => setNoteGateOpen(false)}
        onSubmitted={async () => {
          await handleSubmitted();
          setNoteGateOpen(false);
        }}
      />
    </>
  );
}
